import tauriSourceReadonlyProbeJson from "../../../../artifacts/evidence/TAURI-DESKTOP-SHELL-SOURCE-READONLY-PROBE-001/source-readonly-probe.receipt.json";
import projectStatusJson from "../../../../config/aios-project-status.json";
import capsuleRegistryJson from "../../../../config/capability-capsules.json";
import aiosHomeComprehensionPassJson from "../../../../config/l5-aios-home-comprehension-pass.json";
import cloudSyncActivationDorJson from "../../../../config/l5-cloud-sync-activation-dor.json";
import creativeFlowClarityJson from "../../../../config/l5-creative-flow-clarity.json";
import creativeStarterSurfaceJson from "../../../../config/l5-creative-starter-surface.json";
import ecosystemPublishingDorJson from "../../../../config/l5-ecosystem-publishing-dor.json";
import externalActionSafetyHarnessJson from "../../../../config/l5-external-action-safety-harness.json";
import finalHandoffPackJson from "../../../../config/l5-final-handoff-pack.json";
import localProductPolishRoadmapJson from "../../../../config/l5-local-product-polish-roadmap.json";
import officialSiteLivingBlueprintJson from "../../../../config/l5-official-site-living-blueprint.json";
import pluginEcosystemCatalogJson from "../../../../config/l5-plugin-ecosystem-readonly-catalog.json";
import runtimePreviewTrustJson from "../../../../config/l5-runtime-preview-trust.json";
import userDecisionActivationPackJson from "../../../../config/l5-user-decision-activation-pack.json";
import userDecisionCenterJson from "../../../../config/l5-user-decision-center.json";
import userDecisionPacksReadinessJson from "../../../../config/l5-user-decision-packs-readiness.json";
import userDecisionPromptSurfaceJson from "../../../../config/l5-user-decision-prompt-surface.json";

export type Locale = "zh-CN" | "en-US";
export type VisualSuite = "neural" | "forge" | "atlas";
export type ColorMode = "light" | "dark" | "parchment";
export type StageId = "L0" | "L1" | "L2" | "L3" | "L4" | "L5";
export type Difficulty = "low" | "medium" | "high" | "frontier";
export type SyncState = "live" | "stale" | "fallback" | "demo";

export interface StageProgress {
  id: StageId;
  nameZh: string;
  nameEn: string;
  progress: number;
  taskTotal: number;
  taskDone: number;
  difficulty: Difficulty;
  statusZh: string;
  statusEn: string;
  nextActionZh: string;
  nextActionEn: string;
  weight: number;
}

export interface MissionTask {
  id: string;
  titleZh: string;
  titleEn: string;
  stage: StageId | "DEVX";
  progress: number;
  difficulty: Difficulty;
  state: "active" | "queued" | "done" | "risk";
  nextActionZh: string;
  nextActionEn: string;
}

export interface TauNode {
  id: string;
  labelZh: string;
  labelEn: string;
  state: "ready" | "active" | "queued" | "blocked";
  tauMinutes: number;
  qualityGateZh: string;
  qualityGateEn: string;
}

export interface LongGateStatus {
  id: string;
  titleZh: string;
  titleEn: string;
  status: "guarded" | "running" | "terminal" | "stalled" | "invalid";
  latestActivityAgeMinutes: number;
  terminalFileRequired: boolean;
  observableProgressRequired: boolean;
  stopConditionRequired: boolean;
  safeToWait: boolean;
  messageZh: string;
  messageEn: string;
}

export interface CapabilityCapsuleSummary {
  id: string;
  titleZh: string;
  titleEn: string;
  maturity: "C0" | "C1" | "C2" | "C3" | "C4" | "C5";
  category: string;
  status: string;
  sourceKind: string;
  defaultEnabled: boolean;
  readOnlyByDefault: boolean;
  canWrite: boolean;
  canDeploy: boolean;
  reviewerVerdict: string;
  nextActionZh: string;
  nextActionEn: string;
}

export interface SourceFindingSummary {
  id: string;
  titleZh: string;
  titleEn: string;
  source: string;
  status: string;
  requestsOk: number;
  requestsTotal: number;
  bytesRead: number;
  directoryEntries: number;
  license: string;
  languageMix: Array<{ language: string; sharePercent: number }>;
  canActivateNow: boolean;
  adoptionAllowedNow: boolean;
  licenseApproval: boolean;
  bodyStored: boolean;
  mutatingExternalEffects: number;
  nextGate: string;
  noteZh: string;
  noteEn: string;
  boundaryTagsZh: string[];
  boundaryTagsEn: string[];
}

export interface EcosystemCandidateSummary {
  id: string;
  name: string;
  category: string;
  sourceType: string;
  maturity: "C0" | "C1" | "C2" | "C3";
  currentEnabled: boolean;
  canInstall: boolean;
  canConnect: boolean;
  canExecute: boolean;
  canWrite: boolean;
  licenseStatus: string;
  admissionGate: string;
  risk: string;
}

export interface EcosystemCatalogSummary {
  status: string;
  catalogOnly: boolean;
  candidateCount: number;
  recommendedNotInstalledCount: number;
  installedOrAvailableSkillCount: number;
  openSourceReferenceCount: number;
  enabledCount: number;
  installableNowCount: number;
  connectableNowCount: number;
  executableNowCount: number;
  externalWriteNowCount: number;
  futureActivationControls: string[];
  candidates: EcosystemCandidateSummary[];
}

export interface SyncReadinessSummary {
  status: "dor-only";
  dorOnly: boolean;
  canActivateNow: boolean;
  readinessGateCount: number;
  activationPhaseCount: number;
  nextSafeTask: string;
  boundary: {
    loginNow: false;
    cloudWriteNow: false;
    oauthNow: false;
    secretNow: false;
    runtimeNow: false;
    modelCallNow: false;
    externalWriteNow: false;
  };
  readinessGates: Array<{
    id: string;
    titleZh: string;
    titleEn: string;
    currentState: "blocked";
    requiredBeforeActivation: string[];
    forbiddenShortcut: string;
  }>;
  activationSequence: Array<{
    phase: "A" | "B" | "C" | "D";
    name: string;
    status: string;
    allowedEffect: string;
  }>;
}

export interface PublishingReadinessSummary {
  status: "dor-only";
  dorOnly: boolean;
  canPublishNow: boolean;
  publishingGateCount: number;
  channelCount: number;
  nextSafeTask: string;
  boundary: {
    publishNow: false;
    pluginInstallNow: false;
    marketplaceSubmissionNow: false;
    externalDistributionNow: false;
    externalWriteNow: false;
    paidActionNow: false;
  };
  publishingGates: Array<{
    id: string;
    titleZh: string;
    titleEn: string;
    currentState: "blocked";
    requiredBeforePublish: string[];
    forbiddenShortcut: string;
  }>;
  channels: Array<{
    id: string;
    status: string;
    allowedEffect: string;
    publishNow: boolean;
  }>;
}

export interface FinalHandoffSummary {
  status: "handoff-ready";
  document: string;
  evidenceCount: number;
  visibleCapabilityCount: number;
  remainingUserDecisionCount: number;
  overallProgress: number;
  l5Progress: number;
  l5TaskDone: number;
  l5TaskTotal: number;
  boundary: {
    cloudSyncNow: false;
    ecosystemPublishNow: false;
    externalDistributionNow: false;
    externalWriteNow: false;
    paidActionNow: false;
    secretStorageNow: false;
    deploymentNow: false;
    runtimeNow: false;
    modelCallNow: false;
  };
  remainingUserDecisions: Array<{
    id: string;
    state: "risk";
    requiresUserDecision: true;
    canExecuteNow: false;
    reasonZh: string;
    reasonEn: string;
  }>;
  nextSafeEntryPoints: Array<{
    id: string;
    canStartWithoutUserInput: boolean;
    descriptionZh: string;
    descriptionEn: string;
  }>;
}

export interface LocalProductPolishRoadmapSummary {
  status: "local-product-polish-roadmap-ready";
  sourceEntryPoint: "continue-local-product-polish";
  roadmapItemCount: number;
  safeWithoutUserInputCount: number;
  canExecuteExternalCount: 0;
  externalEffectCount: 0;
  secretValuesEmbeddedCount: 0;
  remainingUserDecisionCount: 3;
  visibleCapability: "local-product-polish-roadmap-readonly";
  boundary: {
    observationalOnly: true;
    approvalAuthority: false;
    storesUserDecisions: false;
    collectsSecrets: false;
    externalWriteNow: false;
    paidActionNow: false;
    secretStorageNow: false;
    deploymentNow: false;
    runtimeNow: false;
    modelCallNow: false;
    toolCallNow: false;
  };
  roadmapItems: Array<{
    id: string;
    priority: number;
    titleZh: string;
    titleEn: string;
    outcomeZh: string;
    outcomeEn: string;
    allowedEffect: "local_ui_docs_tests_only";
    safeWithoutUserInput: true;
    canExecuteExternal: false;
    evidenceSources: string[];
  }>;
  qualityGates: string[];
  defaultBlockers: string[];
}

export interface AiosHomeComprehensionPassSummary {
  status: "aios-home-comprehension-pass-ready";
  sourceEntryPoint: "continue-local-product-polish";
  oneGlanceCardCount: number;
  creativeFlowStepCount: number;
  guardrailCount: number;
  canExecuteNowCount: 0;
  externalEffectCount: 0;
  secretValuesEmbeddedCount: 0;
  visibleCapability: "desktop-aios-home";
  addsNewVisibleCapability: false;
  remainingUserDecisionCount: 3;
  boundary: {
    observationalOnly: true;
    approvalAuthority: false;
    storesUserDecisions: false;
    collectsSecrets: false;
    externalWriteNow: false;
    paidActionNow: false;
    secretStorageNow: false;
    deploymentNow: false;
    runtimeNow: false;
    modelCallNow: false;
    toolCallNow: false;
  };
  oneGlanceCards: Array<{
    id: string;
    titleZh: string;
    titleEn: string;
    bodyZh: string;
    bodyEn: string;
    role: string;
    canExecuteNow: false;
  }>;
  creativeFlowSteps: Array<{
    id: string;
    labelZh: string;
    labelEn: string;
    outputZh: string;
    outputEn: string;
  }>;
  guardrails: string[];
  qualityGates: string[];
}

export interface CreativeFlowClaritySummary {
  status: "creative-flow-clarity-ready";
  sourceEntryPoint: "creative-flow-clarity";
  runwayStepCount: number;
  handoffBridgeCount: number;
  canExecuteNowCount: 0;
  externalEffectCount: 0;
  secretValuesEmbeddedCount: 0;
  visibleCapability: "creative-starter-readonly";
  addsNewVisibleCapability: false;
  remainingUserDecisionCount: 3;
  boundary: {
    observationalOnly: true;
    approvalAuthority: false;
    storesUserDecisions: false;
    collectsSecrets: false;
    externalWriteNow: false;
    paidActionNow: false;
    secretStorageNow: false;
    deploymentNow: false;
    runtimeNow: false;
    modelCallNow: false;
    toolCallNow: false;
  };
  runwaySteps: Array<{
    id: string;
    titleZh: string;
    titleEn: string;
    bodyZh: string;
    bodyEn: string;
    inputZh: string;
    inputEn: string;
    outputZh: string;
    outputEn: string;
    authorityState: string;
    canExecuteNow: false;
  }>;
  handoffBridges: Array<{
    from: string;
    to: string;
    rule: string;
  }>;
  guardrails: string[];
  qualityGates: string[];
}

export interface OfficialSiteLivingBlueprintSummary {
  status: "official-site-living-blueprint-clarity-ready";
  sourceEntryPoint: "official-site-living-blueprint";
  narrativeBlockCount: number;
  proofLaneCount: number;
  canExecuteExternalCount: 0;
  externalEffectCount: 0;
  secretValuesEmbeddedCount: 0;
  visibleCapability: "official-site-living-blueprint";
  addsNewVisibleCapability: false;
  remainingUserDecisionCount: 3;
  boundary: {
    observationalOnly: true;
    approvalAuthority: false;
    storesUserDecisions: false;
    collectsSecrets: false;
    externalWriteNow: false;
    paidActionNow: false;
    secretStorageNow: false;
    deploymentNow: false;
    runtimeNow: false;
    modelCallNow: false;
    toolCallNow: false;
  };
  narrativeBlocks: Array<{
    id: string;
    titleZh: string;
    titleEn: string;
    bodyZh: string;
    bodyEn: string;
    proof: string;
  }>;
  proofLanes: Array<{
    id: string;
    labelZh: string;
    labelEn: string;
    source: string;
  }>;
  guardrails: string[];
  qualityGates: string[];
}

export interface RuntimePreviewTrustSummary {
  status: "runtime-preview-trust-ready";
  sourceEntryPoint: "runtime-preview-trust";
  titleZh: string;
  titleEn: string;
  bodyZh: string;
  bodyEn: string;
  trustCueCount: number;
  stopConditionCount: number;
  receiptSignalCount: number;
  canExecuteNowCount: 0;
  tauriCommandInvocationCount: 0;
  formalTaskWriteCount: 0;
  externalEffectCount: 0;
  secretValuesEmbeddedCount: 0;
  visibleCapability: "desktop-runtime-preview";
  addsNewVisibleCapability: false;
  remainingUserDecisionCount: 3;
  boundary: {
    observationalOnly: true;
    approvalAuthority: false;
    storesUserDecisions: false;
    collectsSecrets: false;
    externalWriteNow: false;
    paidActionNow: false;
    secretStorageNow: false;
    deploymentNow: false;
    runtimeNow: false;
    modelCallNow: false;
    toolCallNow: false;
    tauriCommandInvocationNow: false;
    formalTaskWriteNow: false;
  };
  trustCues: Array<{
    id: string;
    labelZh: string;
    labelEn: string;
    explanationZh: string;
    explanationEn: string;
  }>;
  stopConditions: Array<{
    id: string;
    labelZh: string;
    labelEn: string;
  }>;
  receiptSignals: Array<{
    id: string;
    label: string;
    value: number;
  }>;
  guardrails: string[];
  qualityGates: string[];
}

export interface UserDecisionCenterSummary {
  status: "decision-center-ready";
  decisionCount: number;
  requiresUserDecisionCount: number;
  canExecuteNowCount: number;
  safeAutonomousActionCount: number;
  blockedExternalActionCount: number;
  boundary: {
    approvalAuthority: false;
    storesUserDecisions: false;
    collectsSecrets: false;
    cloudSyncNow: false;
    ecosystemPublishNow: false;
    externalDistributionNow: false;
    externalWriteNow: false;
    paidActionNow: false;
    secretStorageNow: false;
    deploymentNow: false;
    runtimeNow: false;
    modelCallNow: false;
  };
  safeAutonomousActions: Array<{
    id: string;
    canStartWithoutUserInput: boolean;
    allowedEffect: string;
    descriptionZh: string;
    descriptionEn: string;
  }>;
  decisions: Array<{
    id: string;
    state: "risk";
    category: string;
    titleZh: string;
    titleEn: string;
    requiresUserDecision: true;
    canExecuteNow: false;
    currentAllowedEffect: string;
    reasonZh: string;
    reasonEn: string;
    userChoicesRequired: Array<{
      id: string;
      labelZh: string;
      labelEn: string;
      required: true;
      userProvided: false;
      storesSecret: false;
    }>;
    forbiddenUntilComplete: string[];
    activationPath: string[];
  }>;
}

export interface UserDecisionPacksReadinessSummary {
  status: "decision-packs-readiness-ready";
  sourceEntryPoint: "user-decision-packs-readiness";
  titleZh: string;
  titleEn: string;
  bodyZh: string;
  bodyEn: string;
  decisionPackCount: number;
  readyForUserReviewCount: number;
  taskSpecPreparationReadyCount: number;
  requiresUserDecisionCount: 3;
  requiredUserInputCount: number;
  missingUserInputCount: number;
  stopConditionCount: number;
  canStartNowCount: 0;
  canExecuteNowCount: 0;
  externalEffectCount: 0;
  secretValuesEmbeddedCount: 0;
  storesUserDecisionCount: 0;
  visibleCapability: "user-decision-activation-pack-readonly";
  addsNewVisibleCapability: false;
  remainingUserDecisionCount: 3;
  boundary: {
    observationalOnly: true;
    readinessAuditOnly: true;
    approvalAuthority: false;
    storesUserDecisions: false;
    collectsSecrets: false;
    externalWriteNow: false;
    paidActionNow: false;
    secretStorageNow: false;
    deploymentNow: false;
    runtimeNow: false;
    modelCallNow: false;
    toolCallNow: false;
  };
  readinessCards: Array<{
    id: string;
    sourceDecisionId: string;
    sourcePackId: string;
    sourceTemplateId: string;
    category: string;
    titleZh: string;
    titleEn: string;
    state: "waiting-for-user-choice";
    readyForUserReview: true;
    taskSpecPreparationReady: true;
    canStartNow: false;
    canExecuteNow: false;
    missingUserInputs: string[];
    firstSafeStepZh: string;
    firstSafeStepEn: string;
    whyNotNowZh: string;
    whyNotNowEn: string;
    stopConditions: string[];
    noGoActions: string[];
  }>;
  decisionLadder: Array<{
    step: number;
    labelZh: string;
    labelEn: string;
    allowedEffect: string;
  }>;
  guardrails: string[];
  qualityGates: string[];
}

export interface UserDecisionActivationPackSummary {
  status: "activation-pack-ready";
  activationPackCount: number;
  sourceDecisionCount: number;
  canStartNowCount: number;
  externalEffectCount: number;
  secretValuesEmbeddedCount: number;
  copyPromptsCount: number;
  boundary: {
    approvalAuthority: false;
    storesUserDecisions: false;
    collectsSecrets: false;
    embedsSecretValues: false;
    cloudSyncNow: false;
    ecosystemPublishNow: false;
    externalDistributionNow: false;
    externalWriteNow: false;
    paidActionNow: false;
    secretStorageNow: false;
    deploymentNow: false;
    runtimeNow: false;
    modelCallNow: false;
  };
  packs: Array<{
    id: string;
    sourceDecisionId: string;
    category: string;
    titleZh: string;
    titleEn: string;
    userDecisionRequired: true;
    canStartNow: false;
    currentAllowedEffect: string;
    copyPromptZh: string;
    copyPromptEn: string;
    requiredUserInputs: string[];
    activationSequence: string[];
    localPreparationOutputs: string[];
    forbiddenActionsUntilUserGo: string[];
    stopConditions: string[];
  }>;
}

export interface UserDecisionPromptSurfaceSummary {
  status: "prompt-surface-ready";
  promptSurfaceCount: number;
  sourcePackCount: number;
  readonlyTextareaRequired: true;
  buttonCountAllowed: 0;
  clipboardWriteAllowed: false;
  formSubmitAllowed: false;
  externalEffectCount: 0;
  secretValuesEmbeddedCount: 0;
  boundary: {
    clipboardWrite: false;
    formSubmit: false;
    storesUserDecisions: false;
    collectsSecrets: false;
    embedsSecretValues: false;
    externalWriteNow: false;
    paidActionNow: false;
    runtimeNow: false;
  };
  prompts: Array<{
    id: string;
    sourcePackId: string;
    sourceDecisionId: string;
    titleZh: string;
    titleEn: string;
    readonly: true;
    storesUserChoice: false;
    containsSecretValue: false;
  }>;
}

export interface ExternalActionSafetyHarnessSummary {
  status: "safety-harness-ready";
  controlledActionCount: number;
  canExecuteNowCount: 0;
  externalEffectCount: 0;
  secretValuesEmbeddedCount: 0;
  preconditionTotal: number;
  defaultResult: string;
  boundary: {
    observationalOnly: true;
    localCheckOnly: true;
    failClosed: true;
    approvalAuthority: false;
    storesUserDecisions: false;
    collectsSecrets: false;
    externalWriteNow: false;
    paidActionNow: false;
    secretStorageNow: false;
    deploymentNow: false;
    runtimeNow: false;
  };
  contract: {
    explicitUserGoRequired: true;
    independentReviewerRequired: true;
    evidenceRequired: true;
    rollbackRequired: true;
    noExternalEffectsDuringCheck: true;
  };
  controlledActions: Array<{
    id: string;
    decisionId: string;
    templateId: string;
    templatePath: string;
    evidenceTarget: string;
    canExecuteNow: false;
    blockedUntilAllPreconditionsPass: true;
    requiredPreconditions: string[];
    forbiddenWithoutUserGo: string[];
  }>;
}

export interface CreativeStarterSurfaceSummary {
  status: "creative-starter-ready";
  starterPathCount: number;
  canExecuteNowCount: 0;
  externalEffectCount: 0;
  secretValuesEmbeddedCount: 0;
  taskSpecDraftOnly: true;
  localModelAdvisoryOnly: true;
  runtimePreviewOnly: true;
  boundary: {
    observationalOnly: true;
    approvalAuthority: false;
    storesUserDecisions: false;
    collectsSecrets: false;
    externalWriteNow: false;
    paidActionNow: false;
    runtimeNow: false;
    modelCallNow: false;
    toolCallNow: false;
  };
  startPaths: Array<{
    id: string;
    titleZh: string;
    titleEn: string;
    intentZh: string;
    intentEn: string;
    inputZh: string;
    inputEn: string;
    outputZh: string;
    outputEn: string;
    currentAllowedEffect: string;
    canExecuteNow: false;
    gate: string;
    uses: string[];
  }>;
  loopStepsZh: string[];
  loopStepsEn: string[];
  defaultBlockers: string[];
}

export interface SiteState {
  schemaVersion: 1;
  generatedAt: string;
  sourceMode: "embedded-snapshot" | "api" | "cloudflare-d1-mirror" | "fallback";
  formalAuthority: false;
  statusAuthority: "observational";
  mutationAuthority: false;
  currentStage: StageId;
  overallProgress: number;
  liveCadenceSeconds: number;
  stages: StageProgress[];
  currentTasks: MissionTask[];
  nextTasks: MissionTask[];
  tauChain: TauNode[];
  longGates: LongGateStatus[];
  capabilityCapsules: CapabilityCapsuleSummary[];
  sourceFindings: SourceFindingSummary[];
  ecosystemCatalog: EcosystemCatalogSummary;
  syncReadiness: SyncReadinessSummary;
  publishingReadiness: PublishingReadinessSummary;
  finalHandoff: FinalHandoffSummary;
  localProductPolishRoadmap: LocalProductPolishRoadmapSummary;
  aiosHomeComprehensionPass: AiosHomeComprehensionPassSummary;
  creativeFlowClarity: CreativeFlowClaritySummary;
  officialSiteLivingBlueprint: OfficialSiteLivingBlueprintSummary;
  runtimePreviewTrust: RuntimePreviewTrustSummary;
  userDecisionCenter: UserDecisionCenterSummary;
  userDecisionPacksReadiness: UserDecisionPacksReadinessSummary;
  userDecisionActivationPack: UserDecisionActivationPackSummary;
  userDecisionPromptSurface: UserDecisionPromptSurfaceSummary;
  externalActionSafetyHarness: ExternalActionSafetyHarnessSummary;
  creativeStarterSurface: CreativeStarterSurfaceSummary;
  features: Array<{
    id: string;
    titleZh: string;
    titleEn: string;
    bodyZh: string;
    bodyEn: string;
  }>;
  risks: Array<{
    id: string;
    severity: "low" | "medium" | "high" | "critical";
    titleZh: string;
    titleEn: string;
    mitigationZh: string;
    mitigationEn: string;
  }>;
}

interface ProjectStatusConfig {
  schema_version: 1;
  generated_at: string;
  source_mode: SiteState["sourceMode"];
  formal_authority: false;
  current_stage: StageId;
  overall_progress: number;
  live_cadence_seconds: number;
  stage_weights: Record<StageId, number>;
  authority_boundary: {
    status_authority: "observational";
    mutation_authority: false;
  };
  stages: Array<{
    id: StageId;
    name_zh: string;
    name_en: string;
    progress: number;
    task_total: number;
    task_done: number;
    difficulty: Difficulty;
    status_zh: string;
    status_en: string;
    next_action_zh: string;
    next_action_en: string;
  }>;
  current_tasks: RawMissionTask[];
  next_tasks: RawMissionTask[];
  tau_chain: Array<{
    id: string;
    label_zh: string;
    label_en: string;
    state: TauNode["state"];
    tau_minutes: number;
    quality_gate_zh: string;
    quality_gate_en: string;
  }>;
  long_gates: Array<{
    id: string;
    title_zh: string;
    title_en: string;
    status: LongGateStatus["status"];
    latest_activity_age_minutes: number;
    terminal_file_required: boolean;
    observable_progress_required: boolean;
    stop_condition_required: boolean;
    safe_to_wait: boolean;
    message_zh: string;
    message_en: string;
  }>;
  risks: Array<{
    id: string;
    severity: "low" | "medium" | "high" | "critical";
    title_zh: string;
    title_en: string;
    mitigation_zh: string;
    mitigation_en: string;
  }>;
}

interface RawMissionTask {
  id: string;
  title_zh: string;
  title_en: string;
  stage: StageId | "DEVX";
  progress: number;
  difficulty: Difficulty;
  state: MissionTask["state"];
  next_action_zh: string;
  next_action_en: string;
}

interface CapsuleRegistryConfig {
  capsules: Array<{
    id: string;
    title_zh: string;
    title_en: string;
    maturity: CapabilityCapsuleSummary["maturity"];
    category: string;
    status: string;
    source: { kind: string };
    permission_envelope: {
      default_enabled: boolean;
      read_only_by_default: boolean;
      can_write: boolean;
      can_deploy: boolean;
    };
    reviewer_verdict: string;
    next_action_zh: string;
    next_action_en: string;
  }>;
}

interface PluginEcosystemCatalogConfig {
  status: string;
  authority_boundary: {
    catalog_only: boolean;
  };
  future_activation_controls: string[];
  candidates: Array<{
    id: string;
    name: string;
    source_type: string;
    category: string;
    maturity: EcosystemCandidateSummary["maturity"];
    license: {
      status: string;
    };
    permission_envelope: {
      current_enabled: boolean;
      can_install: boolean;
      can_connect: boolean;
      can_execute: boolean;
      can_write: boolean;
    };
    admission_gate: string;
    risk: string;
  }>;
  summary: {
    candidate_count: number;
    recommended_not_installed_count: number;
    installed_or_available_skill_count: number;
    open_source_reference_count: number;
    default_enabled_count: number;
    installable_now_count: number;
    connectable_now_count: number;
    executable_now_count: number;
    external_write_now_count: number;
  };
}

interface CloudSyncActivationDorConfig {
  status: "dor-only";
  authority_boundary: {
    dor_only: boolean;
    real_account_connected: false;
    cloud_write: false;
    oauth_flow: false;
    secret_storage: false;
    runtime_activation: false;
    model_call: false;
    external_write: false;
  };
  activation_readiness: Array<{
    id: string;
    title_zh: string;
    title_en: string;
    current_state: "blocked";
    required_before_activation: string[];
    forbidden_shortcut: string;
  }>;
  activation_sequence: Array<{
    phase: "A" | "B" | "C" | "D";
    name: string;
    status: string;
    allowed_effect: string;
  }>;
  current_decision: {
    can_activate_now: boolean;
    next_safe_task: string;
  };
}

interface EcosystemPublishingDorConfig {
  status: "dor-only";
  authority_boundary: {
    dor_only: boolean;
    publishing_enabled: false;
    marketplace_submission: false;
    plugin_install: false;
    external_distribution: false;
    external_write: false;
    paid_action: false;
  };
  publishing_readiness: Array<{
    id: string;
    title_zh: string;
    title_en: string;
    current_state: "blocked";
    required_before_publish: string[];
    forbidden_shortcut: string;
  }>;
  channels: Array<{
    id: string;
    status: string;
    allowed_effect: string;
    publish_now: boolean;
  }>;
  current_decision: {
    can_publish_now: boolean;
    next_safe_task: string;
  };
}

interface FinalHandoffPackConfig {
  status: "handoff-ready";
  document: string;
  authority_boundary: {
    cloud_sync: false;
    ecosystem_publish: false;
    external_distribution: false;
    external_write: false;
    paid_action: false;
    secret_storage: false;
    deployment: false;
    runtime_activation: false;
    model_call: false;
  };
  progress_after_task: {
    overall_progress: number;
    l5_progress: number;
    l5_task_done: number;
    l5_task_total: number;
  };
  evidence_index: string[];
  visible_capabilities: string[];
  remaining_user_decisions: Array<{
    id: string;
    state: "risk";
    requires_user_decision: true;
    can_execute_now: false;
    reason_zh: string;
    reason_en: string;
  }>;
  next_safe_entry_points: Array<{
    id: string;
    can_start_without_user_input: boolean;
    description_zh: string;
    description_en: string;
  }>;
}

interface LocalProductPolishRoadmapConfig {
  status: "local-product-polish-roadmap-ready";
  source_entry_point: "continue-local-product-polish";
  summary: {
    roadmap_item_count: number;
    safe_without_user_input_count: number;
    can_execute_external_count: 0;
    external_effect_count: 0;
    secret_values_embedded_count: 0;
    remaining_user_decision_count: 3;
    visible_capability: "local-product-polish-roadmap-readonly";
  };
  authority_boundary: {
    observational_only: true;
    approval_authority: false;
    stores_user_decisions: false;
    collects_secrets: false;
    external_write: false;
    paid_action: false;
    secret_storage: false;
    deployment: false;
    runtime_activation: false;
    model_call: false;
    tool_call: false;
  };
  roadmap_items: Array<{
    id: string;
    priority: number;
    title_zh: string;
    title_en: string;
    outcome_zh: string;
    outcome_en: string;
    allowed_effect: "local_ui_docs_tests_only";
    safe_without_user_input: true;
    can_execute_external: false;
    evidence_sources: string[];
  }>;
  quality_gates: string[];
  default_blockers: string[];
}

interface AiosHomeComprehensionPassConfig {
  status: "aios-home-comprehension-pass-ready";
  source_entry_point: "continue-local-product-polish";
  summary: {
    one_glance_card_count: number;
    creative_flow_step_count: number;
    guardrail_count: number;
    can_execute_now_count: 0;
    external_effect_count: 0;
    secret_values_embedded_count: 0;
    visible_capability: "desktop-aios-home";
    adds_new_visible_capability: false;
    remaining_user_decision_count: 3;
  };
  authority_boundary: {
    observational_only: true;
    approval_authority: false;
    stores_user_decisions: false;
    collects_secrets: false;
    external_write: false;
    paid_action: false;
    secret_storage: false;
    deployment: false;
    runtime_activation: false;
    model_call: false;
    tool_call: false;
  };
  one_glance_cards: Array<{
    id: string;
    title_zh: string;
    title_en: string;
    body_zh: string;
    body_en: string;
    role: string;
    can_execute_now: false;
  }>;
  creative_flow_steps: Array<{
    id: string;
    label_zh: string;
    label_en: string;
    output_zh: string;
    output_en: string;
  }>;
  guardrails: string[];
  quality_gates: string[];
}

interface CreativeFlowClarityConfig {
  status: "creative-flow-clarity-ready";
  source_entry_point: "creative-flow-clarity";
  summary: {
    runway_step_count: number;
    handoff_bridge_count: number;
    can_execute_now_count: 0;
    external_effect_count: 0;
    secret_values_embedded_count: 0;
    visible_capability: "creative-starter-readonly";
    adds_new_visible_capability: false;
    remaining_user_decision_count: 3;
  };
  authority_boundary: {
    observational_only: true;
    approval_authority: false;
    stores_user_decisions: false;
    collects_secrets: false;
    external_write: false;
    paid_action: false;
    secret_storage: false;
    deployment: false;
    runtime_activation: false;
    model_call: false;
    tool_call: false;
  };
  runway_steps: Array<{
    id: string;
    title_zh: string;
    title_en: string;
    body_zh: string;
    body_en: string;
    input_zh: string;
    input_en: string;
    output_zh: string;
    output_en: string;
    authority_state: string;
    can_execute_now: false;
  }>;
  handoff_bridges: Array<{
    from: string;
    to: string;
    rule: string;
  }>;
  guardrails: string[];
  quality_gates: string[];
}

interface OfficialSiteLivingBlueprintConfig {
  status: "official-site-living-blueprint-clarity-ready";
  source_entry_point: "official-site-living-blueprint";
  summary: {
    narrative_block_count: number;
    proof_lane_count: number;
    can_execute_external_count: 0;
    external_effect_count: 0;
    secret_values_embedded_count: 0;
    visible_capability: "official-site-living-blueprint";
    adds_new_visible_capability: false;
    remaining_user_decision_count: 3;
  };
  authority_boundary: {
    observational_only: true;
    approval_authority: false;
    stores_user_decisions: false;
    collects_secrets: false;
    external_write: false;
    paid_action: false;
    secret_storage: false;
    deployment: false;
    runtime_activation: false;
    model_call: false;
    tool_call: false;
  };
  narrative_blocks: Array<{
    id: string;
    title_zh: string;
    title_en: string;
    body_zh: string;
    body_en: string;
    proof: string;
  }>;
  proof_lanes: Array<{
    id: string;
    label_zh: string;
    label_en: string;
    source: string;
  }>;
  guardrails: string[];
  quality_gates: string[];
}

interface RuntimePreviewTrustConfig {
  status: "runtime-preview-trust-ready";
  source_entry_point: "runtime-preview-trust";
  title_zh: string;
  title_en: string;
  body_zh: string;
  body_en: string;
  summary: {
    trust_cue_count: number;
    stop_condition_count: number;
    receipt_signal_count: number;
    can_execute_now_count: 0;
    tauri_command_invocation_count: 0;
    formal_task_write_count: 0;
    external_effect_count: 0;
    secret_values_embedded_count: 0;
    remaining_user_decision_count: 3;
    visible_capability: "desktop-runtime-preview";
    adds_new_visible_capability: false;
  };
  authority_boundary: {
    observational_only: true;
    approval_authority: false;
    stores_user_decisions: false;
    collects_secrets: false;
    external_write: false;
    paid_action: false;
    secret_storage: false;
    deployment: false;
    runtime_activation: false;
    model_call: false;
    tool_call: false;
    tauri_command_invocation: false;
    formal_task_write: false;
  };
  trust_cues: Array<{
    id: string;
    label_zh: string;
    label_en: string;
    explanation_zh: string;
    explanation_en: string;
  }>;
  stop_conditions: Array<{
    id: string;
    label_zh: string;
    label_en: string;
  }>;
  receipt_signals: Array<{
    id: string;
    label: string;
    value: number;
  }>;
  guardrails: string[];
  quality_gates: string[];
}

interface UserDecisionCenterConfig {
  status: "decision-center-ready";
  summary: {
    decision_count: number;
    requires_user_decision_count: number;
    can_execute_now_count: number;
    safe_autonomous_action_count: number;
    blocked_external_action_count: number;
  };
  authority_boundary: {
    approval_authority: false;
    stores_user_decisions: false;
    collects_secrets: false;
    cloud_sync: false;
    ecosystem_publish: false;
    external_distribution: false;
    external_write: false;
    paid_action: false;
    secret_storage: false;
    deployment: false;
    runtime_activation: false;
    model_call: false;
  };
  safe_autonomous_actions: Array<{
    id: string;
    can_start_without_user_input: boolean;
    allowed_effect: string;
    description_zh: string;
    description_en: string;
  }>;
  decisions: Array<{
    id: string;
    state: "risk";
    category: string;
    title_zh: string;
    title_en: string;
    requires_user_decision: true;
    can_execute_now: false;
    current_allowed_effect: string;
    reason_zh: string;
    reason_en: string;
    user_choices_required: Array<{
      id: string;
      label_zh: string;
      label_en: string;
      required: true;
      user_provided: false;
      stores_secret: false;
    }>;
    forbidden_until_complete: string[];
    activation_path: string[];
  }>;
}

interface UserDecisionPacksReadinessConfig {
  status: "decision-packs-readiness-ready";
  source_entry_point: "user-decision-packs-readiness";
  title_zh: string;
  title_en: string;
  body_zh: string;
  body_en: string;
  summary: {
    decision_pack_count: number;
    ready_for_user_review_count: number;
    task_spec_preparation_ready_count: number;
    requires_user_decision_count: 3;
    required_user_input_count: number;
    missing_user_input_count: number;
    stop_condition_count: number;
    can_start_now_count: 0;
    can_execute_now_count: 0;
    external_effect_count: 0;
    secret_values_embedded_count: 0;
    stores_user_decision_count: 0;
    visible_capability: "user-decision-activation-pack-readonly";
    adds_new_visible_capability: false;
    remaining_user_decision_count: 3;
  };
  authority_boundary: {
    observational_only: true;
    readiness_audit_only: true;
    approval_authority: false;
    stores_user_decisions: false;
    collects_secrets: false;
    external_write: false;
    paid_action: false;
    secret_storage: false;
    deployment: false;
    runtime_activation: false;
    model_call: false;
    tool_call: false;
  };
  readiness_cards: Array<{
    id: string;
    source_decision_id: string;
    source_pack_id: string;
    source_template_id: string;
    category: string;
    title_zh: string;
    title_en: string;
    state: "waiting-for-user-choice";
    ready_for_user_review: true;
    task_spec_preparation_ready: true;
    can_start_now: false;
    can_execute_now: false;
    missing_user_inputs: string[];
    first_safe_step_zh: string;
    first_safe_step_en: string;
    why_not_now_zh: string;
    why_not_now_en: string;
    stop_conditions: string[];
    no_go_actions: string[];
  }>;
  decision_ladder: Array<{
    step: number;
    label_zh: string;
    label_en: string;
    allowed_effect: string;
  }>;
  guardrails: string[];
  quality_gates: string[];
}

interface UserDecisionActivationPackConfig {
  status: "activation-pack-ready";
  summary: {
    activation_pack_count: number;
    source_decision_count: number;
    can_start_now_count: number;
    external_effect_count: number;
    secret_values_embedded_count: number;
    copy_prompts_count: number;
  };
  authority_boundary: {
    approval_authority: false;
    stores_user_decisions: false;
    collects_secrets: false;
    embeds_secret_values: false;
    cloud_sync: false;
    ecosystem_publish: false;
    external_distribution: false;
    external_write: false;
    paid_action: false;
    secret_storage: false;
    deployment: false;
    runtime_activation: false;
    model_call: false;
  };
  packs: Array<{
    id: string;
    source_decision_id: string;
    category: string;
    title_zh: string;
    title_en: string;
    user_decision_required: true;
    can_start_now: false;
    current_allowed_effect: string;
    copy_prompt_zh: string;
    copy_prompt_en: string;
    required_user_inputs: string[];
    activation_sequence: string[];
    local_preparation_outputs: string[];
    forbidden_actions_until_user_go: string[];
    stop_conditions: string[];
  }>;
}

interface UserDecisionPromptSurfaceConfig {
  status: "prompt-surface-ready";
  summary: {
    prompt_surface_count: number;
    source_pack_count: number;
    readonly_textarea_required: true;
    button_count_allowed: 0;
    clipboard_write_allowed: false;
    form_submit_allowed: false;
    external_effect_count: 0;
    secret_values_embedded_count: 0;
  };
  authority_boundary: {
    clipboard_write: false;
    form_submit: false;
    stores_user_decisions: false;
    collects_secrets: false;
    embeds_secret_values: false;
    external_write: false;
    paid_action: false;
    runtime_activation: false;
  };
  prompts: Array<{
    id: string;
    source_pack_id: string;
    source_decision_id: string;
    title_zh: string;
    title_en: string;
    readonly: true;
    stores_user_choice: false;
    contains_secret_value: false;
  }>;
}

interface ExternalActionSafetyHarnessConfig {
  status: "safety-harness-ready";
  summary: {
    controlled_action_count: number;
    can_execute_now_count: 0;
    external_effect_count: 0;
    secret_values_embedded_count: 0;
    precondition_total: number;
    default_result: string;
  };
  authority_boundary: {
    observational_only: true;
    local_check_only: true;
    fail_closed: true;
    approval_authority: false;
    stores_user_decisions: false;
    collects_secrets: false;
    external_write: false;
    paid_action: false;
    secret_storage: false;
    deployment: false;
    runtime_activation: false;
  };
  harness_contract: {
    explicit_user_go_required: true;
    independent_reviewer_required: true;
    evidence_required: true;
    rollback_required: true;
    no_external_effects_during_check: true;
  };
  controlled_actions: Array<{
    id: string;
    decision_id: string;
    template_id: string;
    template_path: string;
    evidence_target: string;
    can_execute_now: false;
    blocked_until_all_preconditions_pass: true;
    required_preconditions: string[];
    forbidden_without_user_go: string[];
  }>;
}

interface CreativeStarterSurfaceConfig {
  status: "creative-starter-ready";
  summary: {
    starter_path_count: number;
    can_execute_now_count: 0;
    external_effect_count: 0;
    secret_values_embedded_count: 0;
    task_spec_draft_only: true;
    local_model_advisory_only: true;
    runtime_preview_only: true;
  };
  authority_boundary: {
    observational_only: true;
    approval_authority: false;
    stores_user_decisions: false;
    collects_secrets: false;
    external_write: false;
    paid_action: false;
    runtime_activation: false;
    model_call: false;
    tool_call: false;
  };
  start_paths: Array<{
    id: string;
    title_zh: string;
    title_en: string;
    intent_zh: string;
    intent_en: string;
    input_zh: string;
    input_en: string;
    output_zh: string;
    output_en: string;
    current_allowed_effect: string;
    can_execute_now: false;
    gate: string;
    uses: string[];
  }>;
  creative_loop_contract: {
    steps_zh: string[];
    steps_en: string[];
    default_blockers: string[];
  };
}

const projectStatus = projectStatusJson as ProjectStatusConfig;
const capsuleRegistry = capsuleRegistryJson as CapsuleRegistryConfig;
const cloudSyncActivationDor = cloudSyncActivationDorJson as unknown as CloudSyncActivationDorConfig;
const ecosystemPublishingDor = ecosystemPublishingDorJson as unknown as EcosystemPublishingDorConfig;
const finalHandoffPack = finalHandoffPackJson as unknown as FinalHandoffPackConfig;
const localProductPolishRoadmap = localProductPolishRoadmapJson as unknown as LocalProductPolishRoadmapConfig;
const aiosHomeComprehensionPass = aiosHomeComprehensionPassJson as unknown as AiosHomeComprehensionPassConfig;
const creativeFlowClarity = creativeFlowClarityJson as unknown as CreativeFlowClarityConfig;
const officialSiteLivingBlueprint =
  officialSiteLivingBlueprintJson as unknown as OfficialSiteLivingBlueprintConfig;
const runtimePreviewTrust = runtimePreviewTrustJson as unknown as RuntimePreviewTrustConfig;
const pluginEcosystemCatalog = pluginEcosystemCatalogJson as PluginEcosystemCatalogConfig;
const userDecisionActivationPack =
  userDecisionActivationPackJson as unknown as UserDecisionActivationPackConfig;
const userDecisionCenter = userDecisionCenterJson as unknown as UserDecisionCenterConfig;
const userDecisionPacksReadiness =
  userDecisionPacksReadinessJson as unknown as UserDecisionPacksReadinessConfig;
const userDecisionPromptSurface = userDecisionPromptSurfaceJson as unknown as UserDecisionPromptSurfaceConfig;
const externalActionSafetyHarness =
  externalActionSafetyHarnessJson as unknown as ExternalActionSafetyHarnessConfig;
const creativeStarterSurface = creativeStarterSurfaceJson as unknown as CreativeStarterSurfaceConfig;
const tauriSourceReadonlyProbe = tauriSourceReadonlyProbeJson as {
  task_id: string;
  verdict: {
    status: string;
    can_activate_now: boolean;
    adoption_allowed_now: boolean;
    license_approval: boolean;
    next_gate: string;
  };
  totals: {
    ok_responses: number;
    planned_requests: number;
    total_response_bytes: number;
    source_directory_entries_observed: number;
    mutating_external_effects: number;
  };
  public_findings: {
    repository: {
      full_name: string;
      license: { spdx_id: string };
    };
    languages: {
      languages: Array<{ language: string; share_percent: number }>;
    };
    response_bodies_stored: boolean;
    source_bodies_stored: boolean;
  };
};
const TAURI_SOURCE_FINDINGS_CURRENT_NEXT_GATE = "TAURI-DESKTOP-SHELL-CONTROLLED-ADOPTION-SPEC-001";

function mapTask(task: RawMissionTask): MissionTask {
  return {
    id: task.id,
    titleZh: task.title_zh,
    titleEn: task.title_en,
    stage: task.stage,
    progress: task.progress,
    difficulty: task.difficulty,
    state: task.state,
    nextActionZh: task.next_action_zh,
    nextActionEn: task.next_action_en,
  };
}

export const siteState: SiteState = {
  schemaVersion: projectStatus.schema_version,
  generatedAt: projectStatus.generated_at,
  sourceMode: projectStatus.source_mode,
  formalAuthority: projectStatus.formal_authority,
  statusAuthority: projectStatus.authority_boundary.status_authority,
  mutationAuthority: projectStatus.authority_boundary.mutation_authority,
  currentStage: projectStatus.current_stage,
  overallProgress: projectStatus.overall_progress,
  liveCadenceSeconds: projectStatus.live_cadence_seconds,
  stages: projectStatus.stages.map((stage) => ({
    id: stage.id,
    nameZh: stage.name_zh,
    nameEn: stage.name_en,
    progress: stage.progress,
    taskTotal: stage.task_total,
    taskDone: stage.task_done,
    difficulty: stage.difficulty,
    statusZh: stage.status_zh,
    statusEn: stage.status_en,
    nextActionZh: stage.next_action_zh,
    nextActionEn: stage.next_action_en,
    weight: projectStatus.stage_weights[stage.id],
  })),
  currentTasks: projectStatus.current_tasks.map(mapTask),
  nextTasks: projectStatus.next_tasks.map(mapTask),
  tauChain: projectStatus.tau_chain.map((node) => ({
    id: node.id,
    labelZh: node.label_zh,
    labelEn: node.label_en,
    state: node.state,
    tauMinutes: node.tau_minutes,
    qualityGateZh: node.quality_gate_zh,
    qualityGateEn: node.quality_gate_en,
  })),
  longGates: projectStatus.long_gates.map((gate) => ({
    id: gate.id,
    titleZh: gate.title_zh,
    titleEn: gate.title_en,
    status: gate.status,
    latestActivityAgeMinutes: gate.latest_activity_age_minutes,
    terminalFileRequired: gate.terminal_file_required,
    observableProgressRequired: gate.observable_progress_required,
    stopConditionRequired: gate.stop_condition_required,
    safeToWait: gate.safe_to_wait,
    messageZh: gate.message_zh,
    messageEn: gate.message_en,
  })),
  capabilityCapsules: capsuleRegistry.capsules.map((capsule) => ({
    id: capsule.id,
    titleZh: capsule.title_zh,
    titleEn: capsule.title_en,
    maturity: capsule.maturity,
    category: capsule.category,
    status: capsule.status,
    sourceKind: capsule.source.kind,
    defaultEnabled: capsule.permission_envelope.default_enabled,
    readOnlyByDefault: capsule.permission_envelope.read_only_by_default,
    canWrite: capsule.permission_envelope.can_write,
    canDeploy: capsule.permission_envelope.can_deploy,
    reviewerVerdict: capsule.reviewer_verdict,
    nextActionZh: capsule.next_action_zh,
    nextActionEn: capsule.next_action_en,
  })),
  sourceFindings: [
    {
      id: tauriSourceReadonlyProbe.task_id,
      titleZh: "Tauri 桌面壳只读观察",
      titleEn: "Tauri desktop shell read-only observation",
      source: tauriSourceReadonlyProbe.public_findings.repository.full_name,
      status: tauriSourceReadonlyProbe.verdict.status,
      requestsOk: tauriSourceReadonlyProbe.totals.ok_responses,
      requestsTotal: tauriSourceReadonlyProbe.totals.planned_requests,
      bytesRead: tauriSourceReadonlyProbe.totals.total_response_bytes,
      directoryEntries: tauriSourceReadonlyProbe.totals.source_directory_entries_observed,
      license: tauriSourceReadonlyProbe.public_findings.repository.license.spdx_id,
      languageMix: tauriSourceReadonlyProbe.public_findings.languages.languages.slice(0, 5).map((entry) => ({
        language: entry.language,
        sharePercent: entry.share_percent,
      })),
      canActivateNow: tauriSourceReadonlyProbe.verdict.can_activate_now,
      adoptionAllowedNow: tauriSourceReadonlyProbe.verdict.adoption_allowed_now,
      licenseApproval: tauriSourceReadonlyProbe.verdict.license_approval,
      bodyStored:
        tauriSourceReadonlyProbe.public_findings.response_bodies_stored ||
        tauriSourceReadonlyProbe.public_findings.source_bodies_stored,
      mutatingExternalEffects: tauriSourceReadonlyProbe.totals.mutating_external_effects,
      nextGate: TAURI_SOURCE_FINDINGS_CURRENT_NEXT_GATE,
      noteZh:
        "公开 repo / language / directory metadata 与官方文档 HEAD 可达性已进入 AIOS 能力胶囊流水线；仍不是采用、许可证审批、安装、执行或激活。",
      noteEn:
        "Public repo/language/directory metadata and official-doc HEAD reachability have entered the AIOS capsule pipeline; this is still not adoption, license approval, install, execution or activation.",
      boundaryTagsZh: ["未采用", "不 clone", "不下载源码", "不安装/执行", "不激活 Runtime", "不保存正文"],
      boundaryTagsEn: [
        "not adopted",
        "no clone",
        "no source download",
        "no install/execute",
        "no Runtime activation",
        "no body storage",
      ],
    },
  ],
  ecosystemCatalog: {
    status: pluginEcosystemCatalog.status,
    catalogOnly: pluginEcosystemCatalog.authority_boundary.catalog_only,
    candidateCount: pluginEcosystemCatalog.summary.candidate_count,
    recommendedNotInstalledCount: pluginEcosystemCatalog.summary.recommended_not_installed_count,
    installedOrAvailableSkillCount: pluginEcosystemCatalog.summary.installed_or_available_skill_count,
    openSourceReferenceCount: pluginEcosystemCatalog.summary.open_source_reference_count,
    enabledCount: pluginEcosystemCatalog.summary.default_enabled_count,
    installableNowCount: pluginEcosystemCatalog.summary.installable_now_count,
    connectableNowCount: pluginEcosystemCatalog.summary.connectable_now_count,
    executableNowCount: pluginEcosystemCatalog.summary.executable_now_count,
    externalWriteNowCount: pluginEcosystemCatalog.summary.external_write_now_count,
    futureActivationControls: pluginEcosystemCatalog.future_activation_controls,
    candidates: pluginEcosystemCatalog.candidates.slice(0, 6).map((candidate) => ({
      id: candidate.id,
      name: candidate.name,
      category: candidate.category,
      sourceType: candidate.source_type,
      maturity: candidate.maturity,
      currentEnabled: candidate.permission_envelope.current_enabled,
      canInstall: candidate.permission_envelope.can_install,
      canConnect: candidate.permission_envelope.can_connect,
      canExecute: candidate.permission_envelope.can_execute,
      canWrite: candidate.permission_envelope.can_write,
      licenseStatus: candidate.license.status,
      admissionGate: candidate.admission_gate,
      risk: candidate.risk,
    })),
  },
  syncReadiness: {
    status: cloudSyncActivationDor.status,
    dorOnly: cloudSyncActivationDor.authority_boundary.dor_only,
    canActivateNow: cloudSyncActivationDor.current_decision.can_activate_now,
    readinessGateCount: cloudSyncActivationDor.activation_readiness.length,
    activationPhaseCount: cloudSyncActivationDor.activation_sequence.length,
    nextSafeTask: cloudSyncActivationDor.current_decision.next_safe_task,
    boundary: {
      loginNow: cloudSyncActivationDor.authority_boundary.real_account_connected,
      cloudWriteNow: cloudSyncActivationDor.authority_boundary.cloud_write,
      oauthNow: cloudSyncActivationDor.authority_boundary.oauth_flow,
      secretNow: cloudSyncActivationDor.authority_boundary.secret_storage,
      runtimeNow: cloudSyncActivationDor.authority_boundary.runtime_activation,
      modelCallNow: cloudSyncActivationDor.authority_boundary.model_call,
      externalWriteNow: cloudSyncActivationDor.authority_boundary.external_write,
    },
    readinessGates: cloudSyncActivationDor.activation_readiness.slice(0, 4).map((gate) => ({
      id: gate.id,
      titleZh: gate.title_zh,
      titleEn: gate.title_en,
      currentState: gate.current_state,
      requiredBeforeActivation: gate.required_before_activation,
      forbiddenShortcut: gate.forbidden_shortcut,
    })),
    activationSequence: cloudSyncActivationDor.activation_sequence.map((phase) => ({
      phase: phase.phase,
      name: phase.name,
      status: phase.status,
      allowedEffect: phase.allowed_effect,
    })),
  },
  publishingReadiness: {
    status: ecosystemPublishingDor.status,
    dorOnly: ecosystemPublishingDor.authority_boundary.dor_only,
    canPublishNow: ecosystemPublishingDor.current_decision.can_publish_now,
    publishingGateCount: ecosystemPublishingDor.publishing_readiness.length,
    channelCount: ecosystemPublishingDor.channels.length,
    nextSafeTask: ecosystemPublishingDor.current_decision.next_safe_task,
    boundary: {
      publishNow: ecosystemPublishingDor.authority_boundary.publishing_enabled,
      pluginInstallNow: ecosystemPublishingDor.authority_boundary.plugin_install,
      marketplaceSubmissionNow: ecosystemPublishingDor.authority_boundary.marketplace_submission,
      externalDistributionNow: ecosystemPublishingDor.authority_boundary.external_distribution,
      externalWriteNow: ecosystemPublishingDor.authority_boundary.external_write,
      paidActionNow: ecosystemPublishingDor.authority_boundary.paid_action,
    },
    publishingGates: ecosystemPublishingDor.publishing_readiness.slice(0, 4).map((gate) => ({
      id: gate.id,
      titleZh: gate.title_zh,
      titleEn: gate.title_en,
      currentState: gate.current_state,
      requiredBeforePublish: gate.required_before_publish,
      forbiddenShortcut: gate.forbidden_shortcut,
    })),
    channels: ecosystemPublishingDor.channels.map((channel) => ({
      id: channel.id,
      status: channel.status,
      allowedEffect: channel.allowed_effect,
      publishNow: channel.publish_now,
    })),
  },
  finalHandoff: {
    status: finalHandoffPack.status,
    document: finalHandoffPack.document,
    evidenceCount: finalHandoffPack.evidence_index.length,
    visibleCapabilityCount: finalHandoffPack.visible_capabilities.length,
    remainingUserDecisionCount: finalHandoffPack.remaining_user_decisions.length,
    overallProgress: finalHandoffPack.progress_after_task.overall_progress,
    l5Progress: finalHandoffPack.progress_after_task.l5_progress,
    l5TaskDone: finalHandoffPack.progress_after_task.l5_task_done,
    l5TaskTotal: finalHandoffPack.progress_after_task.l5_task_total,
    boundary: {
      cloudSyncNow: finalHandoffPack.authority_boundary.cloud_sync,
      ecosystemPublishNow: finalHandoffPack.authority_boundary.ecosystem_publish,
      externalDistributionNow: finalHandoffPack.authority_boundary.external_distribution,
      externalWriteNow: finalHandoffPack.authority_boundary.external_write,
      paidActionNow: finalHandoffPack.authority_boundary.paid_action,
      secretStorageNow: finalHandoffPack.authority_boundary.secret_storage,
      deploymentNow: finalHandoffPack.authority_boundary.deployment,
      runtimeNow: finalHandoffPack.authority_boundary.runtime_activation,
      modelCallNow: finalHandoffPack.authority_boundary.model_call,
    },
    remainingUserDecisions: finalHandoffPack.remaining_user_decisions.map((decision) => ({
      id: decision.id,
      state: decision.state,
      requiresUserDecision: decision.requires_user_decision,
      canExecuteNow: decision.can_execute_now,
      reasonZh: decision.reason_zh,
      reasonEn: decision.reason_en,
    })),
    nextSafeEntryPoints: finalHandoffPack.next_safe_entry_points.map((entry) => ({
      id: entry.id,
      canStartWithoutUserInput: entry.can_start_without_user_input,
      descriptionZh: entry.description_zh,
      descriptionEn: entry.description_en,
    })),
  },
  localProductPolishRoadmap: {
    status: localProductPolishRoadmap.status,
    sourceEntryPoint: localProductPolishRoadmap.source_entry_point,
    roadmapItemCount: localProductPolishRoadmap.summary.roadmap_item_count,
    safeWithoutUserInputCount: localProductPolishRoadmap.summary.safe_without_user_input_count,
    canExecuteExternalCount: localProductPolishRoadmap.summary.can_execute_external_count,
    externalEffectCount: localProductPolishRoadmap.summary.external_effect_count,
    secretValuesEmbeddedCount: localProductPolishRoadmap.summary.secret_values_embedded_count,
    remainingUserDecisionCount: localProductPolishRoadmap.summary.remaining_user_decision_count,
    visibleCapability: localProductPolishRoadmap.summary.visible_capability,
    boundary: {
      observationalOnly: localProductPolishRoadmap.authority_boundary.observational_only,
      approvalAuthority: localProductPolishRoadmap.authority_boundary.approval_authority,
      storesUserDecisions: localProductPolishRoadmap.authority_boundary.stores_user_decisions,
      collectsSecrets: localProductPolishRoadmap.authority_boundary.collects_secrets,
      externalWriteNow: localProductPolishRoadmap.authority_boundary.external_write,
      paidActionNow: localProductPolishRoadmap.authority_boundary.paid_action,
      secretStorageNow: localProductPolishRoadmap.authority_boundary.secret_storage,
      deploymentNow: localProductPolishRoadmap.authority_boundary.deployment,
      runtimeNow: localProductPolishRoadmap.authority_boundary.runtime_activation,
      modelCallNow: localProductPolishRoadmap.authority_boundary.model_call,
      toolCallNow: localProductPolishRoadmap.authority_boundary.tool_call,
    },
    roadmapItems: localProductPolishRoadmap.roadmap_items.map((item) => ({
      id: item.id,
      priority: item.priority,
      titleZh: item.title_zh,
      titleEn: item.title_en,
      outcomeZh: item.outcome_zh,
      outcomeEn: item.outcome_en,
      allowedEffect: item.allowed_effect,
      safeWithoutUserInput: item.safe_without_user_input,
      canExecuteExternal: item.can_execute_external,
      evidenceSources: item.evidence_sources,
    })),
    qualityGates: localProductPolishRoadmap.quality_gates,
    defaultBlockers: localProductPolishRoadmap.default_blockers,
  },
  aiosHomeComprehensionPass: {
    status: aiosHomeComprehensionPass.status,
    sourceEntryPoint: aiosHomeComprehensionPass.source_entry_point,
    oneGlanceCardCount: aiosHomeComprehensionPass.summary.one_glance_card_count,
    creativeFlowStepCount: aiosHomeComprehensionPass.summary.creative_flow_step_count,
    guardrailCount: aiosHomeComprehensionPass.summary.guardrail_count,
    canExecuteNowCount: aiosHomeComprehensionPass.summary.can_execute_now_count,
    externalEffectCount: aiosHomeComprehensionPass.summary.external_effect_count,
    secretValuesEmbeddedCount: aiosHomeComprehensionPass.summary.secret_values_embedded_count,
    visibleCapability: aiosHomeComprehensionPass.summary.visible_capability,
    addsNewVisibleCapability: aiosHomeComprehensionPass.summary.adds_new_visible_capability,
    remainingUserDecisionCount: aiosHomeComprehensionPass.summary.remaining_user_decision_count,
    boundary: {
      observationalOnly: aiosHomeComprehensionPass.authority_boundary.observational_only,
      approvalAuthority: aiosHomeComprehensionPass.authority_boundary.approval_authority,
      storesUserDecisions: aiosHomeComprehensionPass.authority_boundary.stores_user_decisions,
      collectsSecrets: aiosHomeComprehensionPass.authority_boundary.collects_secrets,
      externalWriteNow: aiosHomeComprehensionPass.authority_boundary.external_write,
      paidActionNow: aiosHomeComprehensionPass.authority_boundary.paid_action,
      secretStorageNow: aiosHomeComprehensionPass.authority_boundary.secret_storage,
      deploymentNow: aiosHomeComprehensionPass.authority_boundary.deployment,
      runtimeNow: aiosHomeComprehensionPass.authority_boundary.runtime_activation,
      modelCallNow: aiosHomeComprehensionPass.authority_boundary.model_call,
      toolCallNow: aiosHomeComprehensionPass.authority_boundary.tool_call,
    },
    oneGlanceCards: aiosHomeComprehensionPass.one_glance_cards.map((card) => ({
      id: card.id,
      titleZh: card.title_zh,
      titleEn: card.title_en,
      bodyZh: card.body_zh,
      bodyEn: card.body_en,
      role: card.role,
      canExecuteNow: card.can_execute_now,
    })),
    creativeFlowSteps: aiosHomeComprehensionPass.creative_flow_steps.map((step) => ({
      id: step.id,
      labelZh: step.label_zh,
      labelEn: step.label_en,
      outputZh: step.output_zh,
      outputEn: step.output_en,
    })),
    guardrails: aiosHomeComprehensionPass.guardrails,
    qualityGates: aiosHomeComprehensionPass.quality_gates,
  },
  creativeFlowClarity: {
    status: creativeFlowClarity.status,
    sourceEntryPoint: creativeFlowClarity.source_entry_point,
    runwayStepCount: creativeFlowClarity.summary.runway_step_count,
    handoffBridgeCount: creativeFlowClarity.summary.handoff_bridge_count,
    canExecuteNowCount: creativeFlowClarity.summary.can_execute_now_count,
    externalEffectCount: creativeFlowClarity.summary.external_effect_count,
    secretValuesEmbeddedCount: creativeFlowClarity.summary.secret_values_embedded_count,
    visibleCapability: creativeFlowClarity.summary.visible_capability,
    addsNewVisibleCapability: creativeFlowClarity.summary.adds_new_visible_capability,
    remainingUserDecisionCount: creativeFlowClarity.summary.remaining_user_decision_count,
    boundary: {
      observationalOnly: creativeFlowClarity.authority_boundary.observational_only,
      approvalAuthority: creativeFlowClarity.authority_boundary.approval_authority,
      storesUserDecisions: creativeFlowClarity.authority_boundary.stores_user_decisions,
      collectsSecrets: creativeFlowClarity.authority_boundary.collects_secrets,
      externalWriteNow: creativeFlowClarity.authority_boundary.external_write,
      paidActionNow: creativeFlowClarity.authority_boundary.paid_action,
      secretStorageNow: creativeFlowClarity.authority_boundary.secret_storage,
      deploymentNow: creativeFlowClarity.authority_boundary.deployment,
      runtimeNow: creativeFlowClarity.authority_boundary.runtime_activation,
      modelCallNow: creativeFlowClarity.authority_boundary.model_call,
      toolCallNow: creativeFlowClarity.authority_boundary.tool_call,
    },
    runwaySteps: creativeFlowClarity.runway_steps.map((step) => ({
      id: step.id,
      titleZh: step.title_zh,
      titleEn: step.title_en,
      bodyZh: step.body_zh,
      bodyEn: step.body_en,
      inputZh: step.input_zh,
      inputEn: step.input_en,
      outputZh: step.output_zh,
      outputEn: step.output_en,
      authorityState: step.authority_state,
      canExecuteNow: step.can_execute_now,
    })),
    handoffBridges: creativeFlowClarity.handoff_bridges.map((bridge) => ({
      from: bridge.from,
      to: bridge.to,
      rule: bridge.rule,
    })),
    guardrails: creativeFlowClarity.guardrails,
    qualityGates: creativeFlowClarity.quality_gates,
  },
  officialSiteLivingBlueprint: {
    status: officialSiteLivingBlueprint.status,
    sourceEntryPoint: officialSiteLivingBlueprint.source_entry_point,
    narrativeBlockCount: officialSiteLivingBlueprint.summary.narrative_block_count,
    proofLaneCount: officialSiteLivingBlueprint.summary.proof_lane_count,
    canExecuteExternalCount: officialSiteLivingBlueprint.summary.can_execute_external_count,
    externalEffectCount: officialSiteLivingBlueprint.summary.external_effect_count,
    secretValuesEmbeddedCount: officialSiteLivingBlueprint.summary.secret_values_embedded_count,
    visibleCapability: officialSiteLivingBlueprint.summary.visible_capability,
    addsNewVisibleCapability: officialSiteLivingBlueprint.summary.adds_new_visible_capability,
    remainingUserDecisionCount: officialSiteLivingBlueprint.summary.remaining_user_decision_count,
    boundary: {
      observationalOnly: officialSiteLivingBlueprint.authority_boundary.observational_only,
      approvalAuthority: officialSiteLivingBlueprint.authority_boundary.approval_authority,
      storesUserDecisions: officialSiteLivingBlueprint.authority_boundary.stores_user_decisions,
      collectsSecrets: officialSiteLivingBlueprint.authority_boundary.collects_secrets,
      externalWriteNow: officialSiteLivingBlueprint.authority_boundary.external_write,
      paidActionNow: officialSiteLivingBlueprint.authority_boundary.paid_action,
      secretStorageNow: officialSiteLivingBlueprint.authority_boundary.secret_storage,
      deploymentNow: officialSiteLivingBlueprint.authority_boundary.deployment,
      runtimeNow: officialSiteLivingBlueprint.authority_boundary.runtime_activation,
      modelCallNow: officialSiteLivingBlueprint.authority_boundary.model_call,
      toolCallNow: officialSiteLivingBlueprint.authority_boundary.tool_call,
    },
    narrativeBlocks: officialSiteLivingBlueprint.narrative_blocks.map((block) => ({
      id: block.id,
      titleZh: block.title_zh,
      titleEn: block.title_en,
      bodyZh: block.body_zh,
      bodyEn: block.body_en,
      proof: block.proof,
    })),
    proofLanes: officialSiteLivingBlueprint.proof_lanes.map((lane) => ({
      id: lane.id,
      labelZh: lane.label_zh,
      labelEn: lane.label_en,
      source: lane.source,
    })),
    guardrails: officialSiteLivingBlueprint.guardrails,
    qualityGates: officialSiteLivingBlueprint.quality_gates,
  },
  runtimePreviewTrust: {
    status: runtimePreviewTrust.status,
    sourceEntryPoint: runtimePreviewTrust.source_entry_point,
    titleZh: runtimePreviewTrust.title_zh,
    titleEn: runtimePreviewTrust.title_en,
    bodyZh: runtimePreviewTrust.body_zh,
    bodyEn: runtimePreviewTrust.body_en,
    trustCueCount: runtimePreviewTrust.summary.trust_cue_count,
    stopConditionCount: runtimePreviewTrust.summary.stop_condition_count,
    receiptSignalCount: runtimePreviewTrust.summary.receipt_signal_count,
    canExecuteNowCount: runtimePreviewTrust.summary.can_execute_now_count,
    tauriCommandInvocationCount: runtimePreviewTrust.summary.tauri_command_invocation_count,
    formalTaskWriteCount: runtimePreviewTrust.summary.formal_task_write_count,
    externalEffectCount: runtimePreviewTrust.summary.external_effect_count,
    secretValuesEmbeddedCount: runtimePreviewTrust.summary.secret_values_embedded_count,
    visibleCapability: runtimePreviewTrust.summary.visible_capability,
    addsNewVisibleCapability: runtimePreviewTrust.summary.adds_new_visible_capability,
    remainingUserDecisionCount: runtimePreviewTrust.summary.remaining_user_decision_count,
    boundary: {
      observationalOnly: runtimePreviewTrust.authority_boundary.observational_only,
      approvalAuthority: runtimePreviewTrust.authority_boundary.approval_authority,
      storesUserDecisions: runtimePreviewTrust.authority_boundary.stores_user_decisions,
      collectsSecrets: runtimePreviewTrust.authority_boundary.collects_secrets,
      externalWriteNow: runtimePreviewTrust.authority_boundary.external_write,
      paidActionNow: runtimePreviewTrust.authority_boundary.paid_action,
      secretStorageNow: runtimePreviewTrust.authority_boundary.secret_storage,
      deploymentNow: runtimePreviewTrust.authority_boundary.deployment,
      runtimeNow: runtimePreviewTrust.authority_boundary.runtime_activation,
      modelCallNow: runtimePreviewTrust.authority_boundary.model_call,
      toolCallNow: runtimePreviewTrust.authority_boundary.tool_call,
      tauriCommandInvocationNow: runtimePreviewTrust.authority_boundary.tauri_command_invocation,
      formalTaskWriteNow: runtimePreviewTrust.authority_boundary.formal_task_write,
    },
    trustCues: runtimePreviewTrust.trust_cues.map((cue) => ({
      id: cue.id,
      labelZh: cue.label_zh,
      labelEn: cue.label_en,
      explanationZh: cue.explanation_zh,
      explanationEn: cue.explanation_en,
    })),
    stopConditions: runtimePreviewTrust.stop_conditions.map((condition) => ({
      id: condition.id,
      labelZh: condition.label_zh,
      labelEn: condition.label_en,
    })),
    receiptSignals: runtimePreviewTrust.receipt_signals.map((signal) => ({
      id: signal.id,
      label: signal.label,
      value: signal.value,
    })),
    guardrails: runtimePreviewTrust.guardrails,
    qualityGates: runtimePreviewTrust.quality_gates,
  },
  userDecisionCenter: {
    status: userDecisionCenter.status,
    decisionCount: userDecisionCenter.summary.decision_count,
    requiresUserDecisionCount: userDecisionCenter.summary.requires_user_decision_count,
    canExecuteNowCount: userDecisionCenter.summary.can_execute_now_count,
    safeAutonomousActionCount: userDecisionCenter.summary.safe_autonomous_action_count,
    blockedExternalActionCount: userDecisionCenter.summary.blocked_external_action_count,
    boundary: {
      approvalAuthority: userDecisionCenter.authority_boundary.approval_authority,
      storesUserDecisions: userDecisionCenter.authority_boundary.stores_user_decisions,
      collectsSecrets: userDecisionCenter.authority_boundary.collects_secrets,
      cloudSyncNow: userDecisionCenter.authority_boundary.cloud_sync,
      ecosystemPublishNow: userDecisionCenter.authority_boundary.ecosystem_publish,
      externalDistributionNow: userDecisionCenter.authority_boundary.external_distribution,
      externalWriteNow: userDecisionCenter.authority_boundary.external_write,
      paidActionNow: userDecisionCenter.authority_boundary.paid_action,
      secretStorageNow: userDecisionCenter.authority_boundary.secret_storage,
      deploymentNow: userDecisionCenter.authority_boundary.deployment,
      runtimeNow: userDecisionCenter.authority_boundary.runtime_activation,
      modelCallNow: userDecisionCenter.authority_boundary.model_call,
    },
    safeAutonomousActions: userDecisionCenter.safe_autonomous_actions.map((action) => ({
      id: action.id,
      canStartWithoutUserInput: action.can_start_without_user_input,
      allowedEffect: action.allowed_effect,
      descriptionZh: action.description_zh,
      descriptionEn: action.description_en,
    })),
    decisions: userDecisionCenter.decisions.map((decision) => ({
      id: decision.id,
      state: decision.state,
      category: decision.category,
      titleZh: decision.title_zh,
      titleEn: decision.title_en,
      requiresUserDecision: decision.requires_user_decision,
      canExecuteNow: decision.can_execute_now,
      currentAllowedEffect: decision.current_allowed_effect,
      reasonZh: decision.reason_zh,
      reasonEn: decision.reason_en,
      userChoicesRequired: decision.user_choices_required.map((choice) => ({
        id: choice.id,
        labelZh: choice.label_zh,
        labelEn: choice.label_en,
        required: choice.required,
        userProvided: choice.user_provided,
        storesSecret: choice.stores_secret,
      })),
      forbiddenUntilComplete: decision.forbidden_until_complete,
      activationPath: decision.activation_path,
    })),
  },
  userDecisionPacksReadiness: {
    status: userDecisionPacksReadiness.status,
    sourceEntryPoint: userDecisionPacksReadiness.source_entry_point,
    titleZh: userDecisionPacksReadiness.title_zh,
    titleEn: userDecisionPacksReadiness.title_en,
    bodyZh: userDecisionPacksReadiness.body_zh,
    bodyEn: userDecisionPacksReadiness.body_en,
    decisionPackCount: userDecisionPacksReadiness.summary.decision_pack_count,
    readyForUserReviewCount: userDecisionPacksReadiness.summary.ready_for_user_review_count,
    taskSpecPreparationReadyCount: userDecisionPacksReadiness.summary.task_spec_preparation_ready_count,
    requiresUserDecisionCount: userDecisionPacksReadiness.summary.requires_user_decision_count,
    requiredUserInputCount: userDecisionPacksReadiness.summary.required_user_input_count,
    missingUserInputCount: userDecisionPacksReadiness.summary.missing_user_input_count,
    stopConditionCount: userDecisionPacksReadiness.summary.stop_condition_count,
    canStartNowCount: userDecisionPacksReadiness.summary.can_start_now_count,
    canExecuteNowCount: userDecisionPacksReadiness.summary.can_execute_now_count,
    externalEffectCount: userDecisionPacksReadiness.summary.external_effect_count,
    secretValuesEmbeddedCount: userDecisionPacksReadiness.summary.secret_values_embedded_count,
    storesUserDecisionCount: userDecisionPacksReadiness.summary.stores_user_decision_count,
    visibleCapability: userDecisionPacksReadiness.summary.visible_capability,
    addsNewVisibleCapability: userDecisionPacksReadiness.summary.adds_new_visible_capability,
    remainingUserDecisionCount: userDecisionPacksReadiness.summary.remaining_user_decision_count,
    boundary: {
      observationalOnly: userDecisionPacksReadiness.authority_boundary.observational_only,
      readinessAuditOnly: userDecisionPacksReadiness.authority_boundary.readiness_audit_only,
      approvalAuthority: userDecisionPacksReadiness.authority_boundary.approval_authority,
      storesUserDecisions: userDecisionPacksReadiness.authority_boundary.stores_user_decisions,
      collectsSecrets: userDecisionPacksReadiness.authority_boundary.collects_secrets,
      externalWriteNow: userDecisionPacksReadiness.authority_boundary.external_write,
      paidActionNow: userDecisionPacksReadiness.authority_boundary.paid_action,
      secretStorageNow: userDecisionPacksReadiness.authority_boundary.secret_storage,
      deploymentNow: userDecisionPacksReadiness.authority_boundary.deployment,
      runtimeNow: userDecisionPacksReadiness.authority_boundary.runtime_activation,
      modelCallNow: userDecisionPacksReadiness.authority_boundary.model_call,
      toolCallNow: userDecisionPacksReadiness.authority_boundary.tool_call,
    },
    readinessCards: userDecisionPacksReadiness.readiness_cards.map((card) => ({
      id: card.id,
      sourceDecisionId: card.source_decision_id,
      sourcePackId: card.source_pack_id,
      sourceTemplateId: card.source_template_id,
      category: card.category,
      titleZh: card.title_zh,
      titleEn: card.title_en,
      state: card.state,
      readyForUserReview: card.ready_for_user_review,
      taskSpecPreparationReady: card.task_spec_preparation_ready,
      canStartNow: card.can_start_now,
      canExecuteNow: card.can_execute_now,
      missingUserInputs: card.missing_user_inputs,
      firstSafeStepZh: card.first_safe_step_zh,
      firstSafeStepEn: card.first_safe_step_en,
      whyNotNowZh: card.why_not_now_zh,
      whyNotNowEn: card.why_not_now_en,
      stopConditions: card.stop_conditions,
      noGoActions: card.no_go_actions,
    })),
    decisionLadder: userDecisionPacksReadiness.decision_ladder.map((step) => ({
      step: step.step,
      labelZh: step.label_zh,
      labelEn: step.label_en,
      allowedEffect: step.allowed_effect,
    })),
    guardrails: userDecisionPacksReadiness.guardrails,
    qualityGates: userDecisionPacksReadiness.quality_gates,
  },
  userDecisionActivationPack: {
    status: userDecisionActivationPack.status,
    activationPackCount: userDecisionActivationPack.summary.activation_pack_count,
    sourceDecisionCount: userDecisionActivationPack.summary.source_decision_count,
    canStartNowCount: userDecisionActivationPack.summary.can_start_now_count,
    externalEffectCount: userDecisionActivationPack.summary.external_effect_count,
    secretValuesEmbeddedCount: userDecisionActivationPack.summary.secret_values_embedded_count,
    copyPromptsCount: userDecisionActivationPack.summary.copy_prompts_count,
    boundary: {
      approvalAuthority: userDecisionActivationPack.authority_boundary.approval_authority,
      storesUserDecisions: userDecisionActivationPack.authority_boundary.stores_user_decisions,
      collectsSecrets: userDecisionActivationPack.authority_boundary.collects_secrets,
      embedsSecretValues: userDecisionActivationPack.authority_boundary.embeds_secret_values,
      cloudSyncNow: userDecisionActivationPack.authority_boundary.cloud_sync,
      ecosystemPublishNow: userDecisionActivationPack.authority_boundary.ecosystem_publish,
      externalDistributionNow: userDecisionActivationPack.authority_boundary.external_distribution,
      externalWriteNow: userDecisionActivationPack.authority_boundary.external_write,
      paidActionNow: userDecisionActivationPack.authority_boundary.paid_action,
      secretStorageNow: userDecisionActivationPack.authority_boundary.secret_storage,
      deploymentNow: userDecisionActivationPack.authority_boundary.deployment,
      runtimeNow: userDecisionActivationPack.authority_boundary.runtime_activation,
      modelCallNow: userDecisionActivationPack.authority_boundary.model_call,
    },
    packs: userDecisionActivationPack.packs.map((pack) => ({
      id: pack.id,
      sourceDecisionId: pack.source_decision_id,
      category: pack.category,
      titleZh: pack.title_zh,
      titleEn: pack.title_en,
      userDecisionRequired: pack.user_decision_required,
      canStartNow: pack.can_start_now,
      currentAllowedEffect: pack.current_allowed_effect,
      copyPromptZh: pack.copy_prompt_zh,
      copyPromptEn: pack.copy_prompt_en,
      requiredUserInputs: pack.required_user_inputs,
      activationSequence: pack.activation_sequence,
      localPreparationOutputs: pack.local_preparation_outputs,
      forbiddenActionsUntilUserGo: pack.forbidden_actions_until_user_go,
      stopConditions: pack.stop_conditions,
    })),
  },
  userDecisionPromptSurface: {
    status: userDecisionPromptSurface.status,
    promptSurfaceCount: userDecisionPromptSurface.summary.prompt_surface_count,
    sourcePackCount: userDecisionPromptSurface.summary.source_pack_count,
    readonlyTextareaRequired: userDecisionPromptSurface.summary.readonly_textarea_required,
    buttonCountAllowed: userDecisionPromptSurface.summary.button_count_allowed,
    clipboardWriteAllowed: userDecisionPromptSurface.summary.clipboard_write_allowed,
    formSubmitAllowed: userDecisionPromptSurface.summary.form_submit_allowed,
    externalEffectCount: userDecisionPromptSurface.summary.external_effect_count,
    secretValuesEmbeddedCount: userDecisionPromptSurface.summary.secret_values_embedded_count,
    boundary: {
      clipboardWrite: userDecisionPromptSurface.authority_boundary.clipboard_write,
      formSubmit: userDecisionPromptSurface.authority_boundary.form_submit,
      storesUserDecisions: userDecisionPromptSurface.authority_boundary.stores_user_decisions,
      collectsSecrets: userDecisionPromptSurface.authority_boundary.collects_secrets,
      embedsSecretValues: userDecisionPromptSurface.authority_boundary.embeds_secret_values,
      externalWriteNow: userDecisionPromptSurface.authority_boundary.external_write,
      paidActionNow: userDecisionPromptSurface.authority_boundary.paid_action,
      runtimeNow: userDecisionPromptSurface.authority_boundary.runtime_activation,
    },
    prompts: userDecisionPromptSurface.prompts.map((prompt) => ({
      id: prompt.id,
      sourcePackId: prompt.source_pack_id,
      sourceDecisionId: prompt.source_decision_id,
      titleZh: prompt.title_zh,
      titleEn: prompt.title_en,
      readonly: prompt.readonly,
      storesUserChoice: prompt.stores_user_choice,
      containsSecretValue: prompt.contains_secret_value,
    })),
  },
  externalActionSafetyHarness: {
    status: externalActionSafetyHarness.status,
    controlledActionCount: externalActionSafetyHarness.summary.controlled_action_count,
    canExecuteNowCount: externalActionSafetyHarness.summary.can_execute_now_count,
    externalEffectCount: externalActionSafetyHarness.summary.external_effect_count,
    secretValuesEmbeddedCount: externalActionSafetyHarness.summary.secret_values_embedded_count,
    preconditionTotal: externalActionSafetyHarness.summary.precondition_total,
    defaultResult: externalActionSafetyHarness.summary.default_result,
    boundary: {
      observationalOnly: externalActionSafetyHarness.authority_boundary.observational_only,
      localCheckOnly: externalActionSafetyHarness.authority_boundary.local_check_only,
      failClosed: externalActionSafetyHarness.authority_boundary.fail_closed,
      approvalAuthority: externalActionSafetyHarness.authority_boundary.approval_authority,
      storesUserDecisions: externalActionSafetyHarness.authority_boundary.stores_user_decisions,
      collectsSecrets: externalActionSafetyHarness.authority_boundary.collects_secrets,
      externalWriteNow: externalActionSafetyHarness.authority_boundary.external_write,
      paidActionNow: externalActionSafetyHarness.authority_boundary.paid_action,
      secretStorageNow: externalActionSafetyHarness.authority_boundary.secret_storage,
      deploymentNow: externalActionSafetyHarness.authority_boundary.deployment,
      runtimeNow: externalActionSafetyHarness.authority_boundary.runtime_activation,
    },
    contract: {
      explicitUserGoRequired: externalActionSafetyHarness.harness_contract.explicit_user_go_required,
      independentReviewerRequired: externalActionSafetyHarness.harness_contract.independent_reviewer_required,
      evidenceRequired: externalActionSafetyHarness.harness_contract.evidence_required,
      rollbackRequired: externalActionSafetyHarness.harness_contract.rollback_required,
      noExternalEffectsDuringCheck:
        externalActionSafetyHarness.harness_contract.no_external_effects_during_check,
    },
    controlledActions: externalActionSafetyHarness.controlled_actions.map((action) => ({
      id: action.id,
      decisionId: action.decision_id,
      templateId: action.template_id,
      templatePath: action.template_path,
      evidenceTarget: action.evidence_target,
      canExecuteNow: action.can_execute_now,
      blockedUntilAllPreconditionsPass: action.blocked_until_all_preconditions_pass,
      requiredPreconditions: action.required_preconditions,
      forbiddenWithoutUserGo: action.forbidden_without_user_go,
    })),
  },
  creativeStarterSurface: {
    status: creativeStarterSurface.status,
    starterPathCount: creativeStarterSurface.summary.starter_path_count,
    canExecuteNowCount: creativeStarterSurface.summary.can_execute_now_count,
    externalEffectCount: creativeStarterSurface.summary.external_effect_count,
    secretValuesEmbeddedCount: creativeStarterSurface.summary.secret_values_embedded_count,
    taskSpecDraftOnly: creativeStarterSurface.summary.task_spec_draft_only,
    localModelAdvisoryOnly: creativeStarterSurface.summary.local_model_advisory_only,
    runtimePreviewOnly: creativeStarterSurface.summary.runtime_preview_only,
    boundary: {
      observationalOnly: creativeStarterSurface.authority_boundary.observational_only,
      approvalAuthority: creativeStarterSurface.authority_boundary.approval_authority,
      storesUserDecisions: creativeStarterSurface.authority_boundary.stores_user_decisions,
      collectsSecrets: creativeStarterSurface.authority_boundary.collects_secrets,
      externalWriteNow: creativeStarterSurface.authority_boundary.external_write,
      paidActionNow: creativeStarterSurface.authority_boundary.paid_action,
      runtimeNow: creativeStarterSurface.authority_boundary.runtime_activation,
      modelCallNow: creativeStarterSurface.authority_boundary.model_call,
      toolCallNow: creativeStarterSurface.authority_boundary.tool_call,
    },
    startPaths: creativeStarterSurface.start_paths.map((startPath) => ({
      id: startPath.id,
      titleZh: startPath.title_zh,
      titleEn: startPath.title_en,
      intentZh: startPath.intent_zh,
      intentEn: startPath.intent_en,
      inputZh: startPath.input_zh,
      inputEn: startPath.input_en,
      outputZh: startPath.output_zh,
      outputEn: startPath.output_en,
      currentAllowedEffect: startPath.current_allowed_effect,
      canExecuteNow: startPath.can_execute_now,
      gate: startPath.gate,
      uses: startPath.uses,
    })),
    loopStepsZh: creativeStarterSurface.creative_loop_contract.steps_zh,
    loopStepsEn: creativeStarterSurface.creative_loop_contract.steps_en,
    defaultBlockers: creativeStarterSurface.creative_loop_contract.default_blockers,
  },
  features: [
    {
      id: "tau-law",
      titleZh: "韬（τ）定律",
      titleEn: "Tau law",
      bodyZh:
        "持续压缩“想法 → 能力接入 → 可信验证 → 用户体验 → 迭代升级”的时间常数 τ，但不压缩证据、安全和回滚。",
      bodyEn:
        "Continuously compress the time constant from idea to trusted capability, without compressing evidence, safety or rollback.",
    },
    {
      id: "capsules",
      titleZh: "Capability Capsule",
      titleEn: "Capability Capsule",
      bodyZh: "GitHub 源、插件、MCP、Cloudflare、IDE、浏览器和 Driver 都先变成 C0-C5 胶囊，再进入 AIOS。",
      bodyEn:
        "GitHub sources, plugins, MCP, Cloudflare, IDE, browsers and drivers become C0-C5 capsules before entering AIOS.",
    },
    {
      id: "local-first",
      titleZh: "Local-first 控制权",
      titleEn: "Local-first control",
      bodyZh: "核心运行、证据、任务状态优先在本地可验证，不把项目命脉交给黑盒云端。",
      bodyEn:
        "Core runtime, evidence and task state remain locally inspectable instead of depending on opaque cloud state.",
    },
    {
      id: "anti-black-hole",
      titleZh: "反黑洞长任务",
      titleEn: "Anti-black-hole long gates",
      bodyZh: "所有长任务必须暴露进度、活动年龄、终态文件和停止条件，不能只说 running。",
      bodyEn:
        "Long tasks must expose progress, activity age, terminal files and stop condition, not just running.",
    },
  ],
  risks: projectStatus.risks.map((risk) => ({
    id: risk.id,
    severity: risk.severity,
    titleZh: risk.title_zh,
    titleEn: risk.title_en,
    mitigationZh: risk.mitigation_zh,
    mitigationEn: risk.mitigation_en,
  })),
};

export function clampProgress(value: number) {
  return Math.max(0, Math.min(100, Math.round(value)));
}

export function snapshotAgeSeconds(state: Pick<SiteState, "generatedAt">, now = new Date()) {
  const generatedAt = Date.parse(state.generatedAt);
  if (!Number.isFinite(generatedAt)) return Number.POSITIVE_INFINITY;
  return Math.max(0, Math.floor((now.getTime() - generatedAt) / 1000));
}

export function deriveSyncState(
  state: Pick<SiteState, "generatedAt" | "sourceMode">,
  now = new Date(),
  staleAfterSeconds = 60 * 30,
): SyncState {
  if (state.sourceMode === "fallback") return "fallback";
  if (state.sourceMode === "embedded-snapshot") return "demo";
  return snapshotAgeSeconds(state, now) > staleAfterSeconds ? "stale" : "live";
}
